import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import {
    Box,
    VStack,
    HStack,
    Text,
    Button,
    Input,
    FormControl,
    FormLabel,
    Card,
    CardHeader,
    CardBody,
    Heading,
    Alert,
    AlertIcon,
    AlertDescription,
    Badge,
    IconButton,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Grid,
    GridItem,
    Divider,
    Code,
    Spinner,
    Link,
    Flex,
    Spacer,
    Center,
    SimpleGrid,
    useColorModeValue,
    Switch,
    Tooltip
} from '@chakra-ui/react';
import {
    AddIcon,
    CopyIcon,
    ExternalLinkIcon,
    RepeatIcon,
    ViewIcon,
    CheckIcon,
    CloseIcon,
    TimeIcon
} from '@chakra-ui/icons';
import axiosClient from '../../../../axios-client';
import { useStateContext } from '../../../../context/ContextProvider';

const SimpleGoogleSheetsManager = () => {
    const { t } = useTranslation();
    const { user } = useStateContext()
    const [configurations, setConfigurations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const { isOpen: isScriptOpen, onOpen: onScriptOpen, onClose: onScriptClose } = useDisclosure();
    const toast = useToast();

    // Form state
    const [newConfig, setNewConfig] = useState({
        sheet_name: '',
        sheet_url: ''
    });

    // Colors
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const cardBg = useColorModeValue('gray.50', 'gray.700');
    const activeBadgeBg = useColorModeValue('green.50', 'green.900');
    const inactiveBadgeBg = useColorModeValue('red.50', 'red.900');

    useEffect(() => {
        if (user?.id) {
            loadConfigurations();
        }

    }, [user?.id]);

    const loadConfigurations = async () => {
        setIsLoading(true);
        try {
            const response = await axiosClient.get(`/api/client-sheets/list/${user?.id}/`);
            // console.log("response : ", response);
            // const data = await response.json();

            if (response.data.success) {
                setConfigurations(response.data.configurations);
            } else {
                toast({
                    title: t("Erreur"),
                    description: t("Erreur lors du chargement des configurations"),
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: t("Erreur de connexion"),
                description: t("Impossible de se connecter au serveur"),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const createConfiguration = async () => {
        if (!newConfig.sheet_name.trim()) {
            toast({
                title: t("Champ requis"),
                description: t("Veuillez entrer un nom pour votre Google Sheets"),
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsLoading(true);
        try {
            const response = await axiosClient.post(`/api/client-sheets/create/${user?.id}/`, newConfig, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();

            if (data.success) {
                toast({
                    title: t("Succès"),
                    description: t("Configuration créée avec succès!"),
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });
                setShowCreateForm(false);
                setNewConfig({ sheet_name: '', sheet_url: '' });
                loadConfigurations();
            } else {
                toast({
                    title: t("Erreur"),
                    description: data.error || 'Erreur lors de la création',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: t("Erreur de connexion"),
                description: t("Impossible de se connecter au serveur"),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleActive = async (configId) => {
        const configToToggle = configurations.find(c => c.id === configId);
        const wouldActivate = !configToToggle?.is_active;
        setTogglingId(configId);
        // Optimistic update for immediate visual feedback
        setConfigurations(prev => prev.map(c => c.id === configId ? { ...c, is_active: wouldActivate } : c));
        try {
            const response = await axiosClient.post(`/api/client-sheets/${configId}/toggle-active/`);
            if (response.data.success) {
                toast({
                    title: t("Statut mis à jour"),
                    description: response.data.message || 'Le statut a été mis à jour avec succès.',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                loadConfigurations();
            } else {
                // Revert optimistic update
                setConfigurations(prev => prev.map(c => c.id === configId ? { ...c, is_active: !wouldActivate } : c));
                toast({
                    title: t("Erreur"),
                    description: response.data.error || 'Erreur lors du changement de statut.',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
            }
        } catch (error) {
            // Revert optimistic update
            setConfigurations(prev => prev.map(c => c.id === configId ? { ...c, is_active: !wouldActivate } : c));
            const errMsg = error.response?.data?.error || 'Impossible de contacter le serveur';
            toast({
                title: t("Erreur"),
                description: errMsg,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setTogglingId(null);
        }
    };

    const copyToClipboard = (text, type) => {
        navigator.clipboard.writeText(text);
        toast({
            title: t("Copié!"),
            description: `${type} copié dans le presse-papiers`,
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    const generateScript = (webhookUrl) => {
        return `// ==============================================
// SCRIPT GOOGLE APPS SCRIPT POUR VOTRE GOOGLE SHEETS
// ==============================================

// CONFIGURATION - NE MODIFIEZ QUE CETTE LIGNE
const WEBHOOK_URL = "${webhookUrl}";

// Nom de la feuille à surveiller
const SHEET_NAME = "Feuille 1";

// Colonnes attendues (ne pas modifier)
const COLUMNS = {
  A: 'nom_complet',       // nom complet
  B: 'email',             // email  
  C: 'pays',              // pays
  D: 'ville',             // ville
  E: 'adresse',           // adresse
  F: 'num_telephone',     // num téléphone
  G: 'code_produit',      // code produit
  H: 'couleur',           // couleur
  I: 'taille',            // taille
  J: 'quantite',          // quantité
  K: 'prix_de_vente',     // prix de vente
  L: 'code_coupon'        // code coupon
};

function onEdit(e) {
  try {
    if (e.source.getActiveSheet().getName() !== SHEET_NAME) return;
    
    const range = e.range;
    const row = range.getRow();
    if (row <= 1) return;
    
    const sheet = e.source.getActiveSheet();
    const rowData = getRowData(sheet, row);
    
    if (!isRowComplete(rowData)) return;
    
    sendToWebhook(rowData, row);
    
  } catch (error) {
    // // console.error("Erreur:", error);
  }
}

function getRowData(sheet, row) {
  const data = {};
  for (let col in COLUMNS) {
    const cellValue = sheet.getRange(col + row).getValue();
    const fieldName = COLUMNS[col];
    
    if (cellValue !== null && cellValue !== "") {
      if (fieldName === 'num_telephone') {
        data[fieldName] = cellValue.toString();
      } else if (fieldName === 'quantite') {
        data[fieldName] = parseInt(cellValue) || 1;
      } else if (fieldName === 'prix_de_vente') {
        data[fieldName] = parseFloat(cellValue) || 0;
      } else {
        data[fieldName] = cellValue.toString().trim();
      }
    }
  }
  return data;
}

function isRowComplete(rowData) {
  const requiredFields = ['nom_complet', 'num_telephone', 'code_produit', 'prix_de_vente', 'quantite'];
  return requiredFields.every(field => rowData[field] && rowData[field] !== "");
}

function sendToWebhook(rowData, row) {
  try {
    const payload = {
      row_data: rowData,
      source_row: row,
      timestamp: new Date().toISOString()
    };
    
    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      payload: JSON.stringify(payload),
      muteHttpExceptions: true
    };
    
    const response = UrlFetchApp.fetch(WEBHOOK_URL, options);
    const responseText = response.getContentText();
    const responseCode = response.getResponseCode();
    
    if (responseCode === 200) {
      const result = JSON.parse(responseText);
      if (result.success) {
        // console.log("✅ Commande envoyée:", result.message);
        markRowAsProcessed(row, "✅ Envoyé");
      } else {
        // // console.error("❌ Erreur:", result.message);
        markRowAsProcessed(row, "❌ Erreur");
      }
    } else {
      // // console.error("❌ Erreur HTTP:", responseCode);
      markRowAsProcessed(row, "❌ Erreur HTTP");
    }
    
  } catch (error) {
    // // console.error("❌ Erreur:", error);
    markRowAsProcessed(row, "❌ Erreur");
  }
}

function markRowAsProcessed(row, status) {
  try {
    const sheet = SpreadsheetApp.getActiveSheet();
    sheet.getRange("M" + row).setValue(status);
    sheet.getRange("N" + row).setValue(new Date());
  } catch (error) {
    // console.log("Impossible de marquer la ligne:", error);
  }
}

function setupHeaders() {
  const sheet = SpreadsheetApp.getActiveSheet();
  const headers = [
    "nom complet", "email", "pays", "ville", "adresse", 
    "num téléphone", "code produit", "couleur", "taille", 
    "quantité", "prix de vente", "code coupon", "Statut", "Date traitement"
  ];
  
  const headerRange = sheet.getRange(1, 1, 1, headers.length);
  headerRange.setValues([headers]);
  headerRange.setBackground("#4285f4");
  headerRange.setFontColor("white");
  headerRange.setFontWeight("bold");
  sheet.autoResizeColumns(1, headers.length);
}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Jamais';
        return new Date(dateString).toLocaleString('fr-FR');
    };

    const getStatusBadge = (activity) => {
        if (!activity || activity.length === 0) {
            return <Badge colorScheme="gray">{t("Aucune activité")}</Badge>;
        }

        const lastActivity = activity[0];
        if (lastActivity.success) {
            return <Badge colorScheme="green">{t("Fonctionnel")}</Badge>;
        } else {
            return <Badge colorScheme="red">{t("Erreur")}</Badge>;
        }
    };

    const openScript = (config) => {
        setSelectedConfig(config);
        onScriptOpen();
    };

    return (
        <Box p={6}>
            <VStack spacing={6} align="stretch">
                {/* Header */}
                <Flex justify="space-between" align="center">
                    <Box>
                        <Heading size="lg" mb={2}>
                            {t("📊 Google Sheets Integration")}
                        </Heading>
                        <Text color="gray.600">
                            Connectez vos Google Sheets pour importer automatiquement vos commandes
                        </Text>
                    </Box>
                    <Button
                        leftIcon={<AddIcon />}
                        colorScheme="blue"
                        onClick={() => setShowCreateForm(!showCreateForm)}
                    >
                        {t("Nouveau Google Sheets")}
                    </Button>
                </Flex>

                {/* Formulaire de création */}
                {showCreateForm && (
                    <Card bg={bgColor} borderColor={borderColor}>
                        <CardHeader>
                            <Heading size="md">{t("Connecter un Google Sheets")}</Heading>
                        </CardHeader>
                        <CardBody>
                            <VStack spacing={4} align="stretch">
                                <FormControl isRequired>
                                    <FormLabel>{t("Nom de votre Google Sheets")}</FormLabel>
                                    <Input
                                        value={newConfig.sheet_name}
                                        onChange={(e) => setNewConfig({ ...newConfig, sheet_name: e.target.value })}
                                        placeholder={t("Ex: Commandes Janvier 2025")}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>{t("URL de votre Google Sheets (optionnel)")}</FormLabel>
                                    <Input
                                        type="url"
                                        value={newConfig.sheet_url}
                                        onChange={(e) => setNewConfig({ ...newConfig, sheet_url: e.target.value })}
                                        placeholder={t("https://docs.google.com/spreadsheets/d/...")}
                                    />
                                </FormControl>

                                <Alert status="info" borderRadius="md">
                                    <AlertIcon />
                                    <Box>
                                        <Text fontWeight="bold" mb={2}>{t("Format requis :")}</Text>
                                        <Text fontSize="sm" mb={2}><strong>{t("Colonnes obligatoires :")}</strong></Text>
                                        <SimpleGrid columns={2} spacing={2} fontSize="sm">
                                            <Text>{t("• nom complet")}</Text>
                                            <Text>{t("• num téléphone")}</Text>
                                            <Text>{t("• code produit")}</Text>
                                            <Text>{t("• prix de vente")}</Text>
                                            <Text>{t("• quantité")}</Text>
                                        </SimpleGrid>
                                        <Text fontSize="sm" mt={2}>
                                            <strong>{t("Colonnes optionnelles :")}</strong> {t("email, pays, ville, adresse, couleur, taille, code coupon")}
                                        </Text>
                                    </Box>
                                </Alert>

                                <HStack>
                                    <Button
                                        colorScheme="blue"
                                        onClick={createConfiguration}
                                        isLoading={isLoading}
                                        loadingText="Création..."
                                    >
                                        {t("Créer la connexion")}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowCreateForm(false)}
                                    >
                                        {t("Annuler")}
                                    </Button>
                                </HStack>
                            </VStack>
                        </CardBody>
                    </Card>
                )}

                {/* Liste des configurations */}
                {configurations.length === 0 ? (
                    <Card bg={bgColor} borderColor={borderColor}>
                        <CardBody>
                            <Center py={8}>
                                <VStack spacing={4}>
                                    <Text fontSize="6xl">📊</Text>
                                    <Heading size="md" textAlign="center">
                                        {t("Aucun Google Sheets connecté")}
                                    </Heading>
                                    <Text color="gray.500" textAlign="center" maxW="md">
                                        Connectez votre premier Google Sheets pour commencer à importer vos commandes automatiquement
                                    </Text>
                                    {JSON.stringify(configurations)}
                                    <Button
                                        leftIcon={<AddIcon />}
                                        colorScheme="blue"
                                        onClick={() => setShowCreateForm(true)}
                                    >
                                        {t("Connecter un Google Sheets")}
                                    </Button>
                                </VStack>
                            </Center>
                        </CardBody>
                    </Card>
                ) : (
                    <VStack spacing={4} align="stretch">
                        {configurations.map((config) => (
                            <Card key={config.id} bg={bgColor} borderColor={borderColor}>
                                <CardHeader>
                                    <Flex justify="space-between" align="start">
                                        <Box flex="1">
                                            <HStack mb={2} spacing={3} align="center">
                                                <Heading size="md">📈 {config.sheet_name}</Heading>
                                                <Flex
                                                    align="center"
                                                    gap={2.5}
                                                    bg={config.is_active ? activeBadgeBg : inactiveBadgeBg}
                                                    px={3}
                                                    py={1}
                                                    rounded="full"
                                                    border="1px solid"
                                                    borderColor={config.is_active ? 'green.200' : 'red.200'}
                                                >
                                                    <Badge
                                                        colorScheme={config.is_active ? 'green' : 'red'}
                                                        variant="solid"
                                                        rounded="full"
                                                        px={2}
                                                        py={0.5}
                                                        fontSize="2xs"
                                                    >
                                                        {config.is_active ? 'ACTIF' : 'INACTIF'}
                                                    </Badge>
                                                    <Tooltip label={config.is_active ? 'Désactiver l\'importation Google Sheets' : 'Activer l\'importation Google Sheets'}>
                                                        <Box display="flex" alignItems="center">
                                                            <Switch
                                                                isChecked={config.is_active}
                                                                onChange={() => handleToggleActive(config.id)}
                                                                isDisabled={togglingId === config.id}
                                                                colorScheme="green"
                                                                size="md"
                                                                cursor="pointer"
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                </Flex>
                                            </HStack>
                                            {config.sheet_url && (
                                                <Link href={config.sheet_url} isExternal color="blue.500" fontSize="sm">
                                                    <HStack spacing={1}>
                                                        <ExternalLinkIcon />
                                                        <Text>{t("Ouvrir Google Sheets")}</Text>
                                                    </HStack>
                                                </Link>
                                            )}
                                        </Box>
                                        {getStatusBadge(config.recent_activity)}
                                    </Flex>
                                </CardHeader>

                                <CardBody>
                                    <VStack spacing={4} align="stretch">
                                        {/* Statistiques */}
                                        <Box bg={cardBg} p={4} borderRadius="md">
                                            <SimpleGrid columns={2} spacing={4}>
                                                <Box>
                                                    <Text fontSize="sm" color="gray.600">Commandes importées</Text>
                                                    <Text fontSize="xl" fontWeight="bold" color="blue.500">
                                                        {config.total_orders_imported}
                                                    </Text>
                                                </Box>
                                                <Box>
                                                    <Text fontSize="sm" color="gray.600">{t("Dernière activité")}</Text>
                                                    <Text fontSize="sm" fontWeight="medium">
                                                        {formatDate(config.last_import)}
                                                    </Text>
                                                </Box>
                                            </SimpleGrid>
                                        </Box>

                                        {/* Activité récente */}
                                        {config.recent_activity && config.recent_activity.length > 0 && (
                                            <Box>
                                                <Text fontWeight="medium" mb={2}>{t("Activité récente :")}</Text>
                                                <VStack spacing={2} align="stretch" maxH="120px" overflowY="auto">
                                                    {config.recent_activity.slice(0, 3).map((activity, index) => (
                                                        <HStack key={index} p={2} bg="white" borderRadius="md" border="1px" borderColor={borderColor}>
                                                            {activity.success ? (
                                                                <CheckIcon color="green.500" />
                                                            ) : (
                                                                <CloseIcon color="red.500" />
                                                            )}
                                                            <Text flex="1" fontSize="sm">
                                                                {activity.order_created ? 'Nouvelle commande créée' :
                                                                    activity.success ? 'Produit ajouté à commande existante' :
                                                                        'Erreur lors du traitement'}
                                                            </Text>
                                                            <Text fontSize="xs" color="gray.500">
                                                                {formatDate(activity.timestamp)}
                                                            </Text>
                                                        </HStack>
                                                    ))}
                                                </VStack>
                                            </Box>
                                        )}

                                        {/* Token et URL */}
                                        <VStack spacing={3} align="stretch">
                                            <Box>
                                                <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                                                    {t("Token de connexion :")}
                                                </Text>
                                                <HStack>
                                                    <Input
                                                        value={config.webhook_token}
                                                        isReadOnly
                                                        bg={cardBg}
                                                        fontSize="xs"
                                                        fontFamily="mono"
                                                        flex="1"
                                                    />
                                                    <IconButton
                                                        aria-label={t("Copier token")}
                                                        icon={<CopyIcon />}
                                                        size="sm"
                                                        onClick={() => copyToClipboard(config.webhook_token, 'Token')}
                                                    />
                                                </HStack>
                                            </Box>

                                            <Box>
                                                <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                                                    {t("URL du webhook :")}
                                                </Text>
                                                <HStack>
                                                    <Input
                                                        value={config.webhook_url}
                                                        isReadOnly
                                                        bg={cardBg}
                                                        fontSize="xs"
                                                        fontFamily="mono"
                                                        flex="1"
                                                    />
                                                    <IconButton
                                                        aria-label={t("Copier URL")}
                                                        icon={<CopyIcon />}
                                                        size="sm"
                                                        onClick={() => copyToClipboard(config.webhook_url, 'URL')}
                                                    />
                                                </HStack>
                                            </Box>
                                        </VStack>

                                        <Divider />

                                        {/* Actions */}
                                        <HStack>
                                            <Button
                                                leftIcon={<ViewIcon />}
                                                size="sm"
                                                variant="outline"
                                                onClick={() => openScript(config)}
                                            >
                                                {t("Voir le script")}
                                            </Button>



                                            <Button
                                                leftIcon={isLoading ? <Spinner size="xs" /> : <RepeatIcon />}
                                                size="sm"
                                                variant="outline"
                                                onClick={loadConfigurations}
                                                isLoading={isLoading}
                                            >
                                                {t("Actualiser")}
                                            </Button>
                                        </HStack>
                                    </VStack>
                                </CardBody>
                            </Card>
                        ))}
                    </VStack>
                )}

                {/* Guide d'utilisation */}
                <Card bg="blue.50" borderColor="blue.200">
                    <CardHeader>
                        <Heading size="md" color="blue.800">
                            {t("🎯 Comment ça marche ?")}
                        </Heading>
                    </CardHeader>
                    <CardBody>
                        <SimpleGrid columns={[1, null, 3]} spacing={6}>
                            <VStack>
                                <Center w="8" h="8" bg="blue.500" color="white" borderRadius="full" fontWeight="bold">
                                    1
                                </Center>
                                <Text fontWeight="medium" textAlign="center" color="black">{t("Créez la connexion")}</Text>
                                <Text fontSize="sm" textAlign="center" color="blue.700">
                                    {t("Cliquez sur \"Nouveau Google Sheets\" et donnez un nom à votre fichier")}
                                </Text>
                            </VStack>
                            <VStack>
                                <Center w="8" h="8" bg="blue.500" color="white" borderRadius="full" fontWeight="bold">
                                    2
                                </Center>
                                <Text fontWeight="medium" textAlign="center" color="black">{t("Installez le script")}</Text>
                                <Text fontSize="sm" textAlign="center" color="blue.700">
                                    {t("Copiez le script fourni dans Google Apps Script de votre feuille")}
                                </Text>
                            </VStack>
                            <VStack>
                                <Center w="8" h="8" bg="blue.500" color="white" borderRadius="full" fontWeight="bold">
                                    3
                                </Center>
                                <Text fontWeight="medium" textAlign="center" color="black">{t("Ajoutez vos commandes")}</Text>
                                <Text fontSize="sm" textAlign="center" color="blue.700">
                                    Chaque ligne ajoutée sera automatiquement importée dans le système
                                </Text>
                            </VStack>
                        </SimpleGrid>
                    </CardBody>
                </Card>
            </VStack>

            {/* Modal pour afficher le script */}
            <Modal isOpen={isScriptOpen} onClose={onScriptClose} size="6xl">
                <ModalOverlay />
                <ModalContent maxW="90vw" maxH="90vh">
                    <ModalHeader>
                        <HStack>
                            <ViewIcon />
                            <Text>Script Google Apps Script pour "{selectedConfig?.sheet_name}"</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />

                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <Alert status="info" borderRadius="md">
                                <AlertIcon />
                                <Box>
                                    <Text fontWeight="bold" mb={2}>{t("Instructions :")}</Text>
                                    <VStack align="start" spacing={1} fontSize="sm">
                                        <Text>{t("1. Ouvrez votre Google Sheets")}</Text>
                                        <Text>{t("2. Allez dans Extensions → Apps Script")}</Text>
                                        <Text>{t("3. Supprimez le code par défaut et collez le script ci-dessous")}</Text>
                                        <Text>{t("4. Sauvegardez le script (Ctrl+S)")}</Text>
                                        <Text>{t("5. Autorisez les permissions demandées")}</Text>
                                        <Text>{t("6. Exécutez la fonction \"setupHeaders\" pour créer les en-têtes")}</Text>
                                        <Text>7. Ajoutez vos données et elles seront automatiquement importées !</Text>
                                    </VStack>
                                </Box>
                            </Alert>

                            <Box
                                bg="gray.900"
                                color="green.300"
                                p={4}
                                borderRadius="md"
                                overflow="auto"
                                maxH="50vh"
                            >
                                <Code
                                    fontSize="xs"
                                    whiteSpace="pre"
                                    color="green.300"
                                    bg="transparent"
                                >
                                    {selectedConfig && generateScript(selectedConfig.webhook_url)}
                                </Code>
                            </Box>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <HStack>
                            <Button
                                leftIcon={<CopyIcon />}
                                colorScheme="blue"
                                onClick={() => {
                                    if (selectedConfig) {
                                        copyToClipboard(generateScript(selectedConfig.webhook_url), 'Script');
                                    }
                                }}
                            >
                                {t("Copier le script")}
                            </Button>
                            <Button variant="outline" onClick={onScriptClose}>
                                {t("Fermer")}
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default SimpleGoogleSheetsManager;